import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { PublicFooter } from "../components/PublicFooter";
import { PublicHeader } from "../components/PublicHeader";

const container: React.CSSProperties = {
  maxWidth: 860,
  margin: "0 auto",
  padding: "0 24px",
};

const sectionStyle: React.CSSProperties = {
  borderTop: "1px solid var(--l-border)",
  padding: "34px 0",
};

const h2Style: React.CSSProperties = {
  color: "var(--l-text)",
  fontSize: "1.35rem",
  fontWeight: 700,
  marginBottom: 12,
};

const paragraphStyle: React.CSSProperties = {
  color: "var(--l-text-muted)",
  fontSize: "1rem",
  lineHeight: 1.8,
  margin: "0 0 14px",
};

const listStyle: React.CSSProperties = {
  color: "var(--l-text-muted)",
  fontSize: "1rem",
  lineHeight: 1.8,
  paddingLeft: 22,
  margin: "0 0 14px",
};

const supportContact = "the support channel listed in the OpenClaw Machines product";

function LegalLayout({
  title,
  description,
  canonicalPath,
  children,
}: {
  title: string;
  description: string;
  canonicalPath: string;
  children: React.ReactNode;
}) {
  const canonicalUrl = `https://openclawmachines.com${canonicalPath}`;

  return (
    <div className="landing min-h-screen" style={{ background: "var(--l-bg)", color: "var(--l-text)" }}>
      <Helmet>
        <title>{title} - OpenClaw Machines</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${title} - OpenClaw Machines`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <PublicHeader variant="theme" />
      <main style={{ padding: "76px 0 96px" }}>
        <div style={container}>
          <Link to="/" style={{ color: "var(--l-accent)", fontSize: 14, fontWeight: 600 }}>
            OpenClaw Machines
          </Link>
          <h1
            style={{
              color: "var(--l-text)",
              fontSize: "clamp(2.2rem, 5vw, 3.4rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
              margin: "18px 0 12px",
            }}
          >
            {title}
          </h1>
          <p style={{ ...paragraphStyle, maxWidth: 680 }}>
            Last updated: June 18, 2026
          </p>
          {children}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={sectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={paragraphStyle}>{children}</p>;
}

function UL({ children }: { children: React.ReactNode }) {
  return <ul style={listStyle}>{children}</ul>;
}

export function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      description="Privacy Policy for OpenClaw Machines, including account data, machine data, integrations, and service usage."
      canonicalPath="/privacy"
    >
      <Section title="Overview">
        <P>
          OpenClaw Machines provides hosted infrastructure for running sandboxed AI agents, web terminals,
          browser workspaces, and connected workflow integrations. This Privacy Policy explains what we
          collect, how we use it, and the choices you have.
        </P>
        <P>
          For questions about this policy or your data, contact us through {supportContact}.
        </P>
      </Section>

      <Section title="Information We Collect">
        <UL>
          <li>Account information, such as your email address, authentication provider, display name, and workspace membership.</li>
          <li>Service configuration, such as machine names, workspace settings, runtime settings, integration settings, and support requests.</li>
          <li>Operational data, such as usage records, logs, diagnostics, IP address, browser/device information, security events, and error reports.</li>
          <li>Credentials you choose to provide, such as API keys, OAuth tokens, and integration secrets. These are used to operate the features you enable.</li>
          <li>Waitlist and communication data, such as email addresses and survey responses submitted through public forms.</li>
        </UL>
      </Section>

      <Section title="Connected Services Data">
        <P>
          If you connect a third-party service, OpenClaw Machines requests access only after you authorize
          the connection. Depending on the integration and permissions you approve, the service may receive
          profile information, account identifiers, metadata, content, event data, files, messages, or other
          information made available by that connected service.
        </P>
        <P>
          OpenClaw Machines uses connected services data only to provide the integrations, agent tools,
          automation, and workflows you enable. The specific data available to an agent depends on the
          connected service, the scopes or permissions you approve, and the instructions you give.
        </P>
        <UL>
          <li>We do not sell connected services data.</li>
          <li>We do not use connected services data for advertising.</li>
          <li>We do not use connected services data to train generalized AI models.</li>
          <li>We transfer connected services data only as necessary to provide the service, comply with law, protect security, or act on your instructions.</li>
          <li>Humans do not review connected services data except with your consent, for security or abuse investigation, to comply with law, or to provide support you request.</li>
        </UL>
      </Section>

      <Section title="How We Use Information">
        <UL>
          <li>Provide, secure, operate, debug, and improve OpenClaw Machines.</li>
          <li>Authenticate users, manage sessions, authorize access, and prevent abuse.</li>
          <li>Provision and manage machines, backups, integrations, browser sessions, and terminal access.</li>
          <li>Respond to support requests and communicate about service changes, security, and account activity.</li>
          <li>Comply with legal obligations and enforce our Terms of Service.</li>
        </UL>
      </Section>

      <Section title="Sharing">
        <P>
          We share information with infrastructure, security, authentication, analytics, communication,
          payment, and integration providers only as needed to provide and operate the service. We may also
          disclose information if required by law, to protect users or the service, or as part of a
          corporate transaction such as a merger or acquisition.
        </P>
        <P>
          When you instruct an agent to access, process, transmit, or disclose information through an
          integration or external service, you are directing that transfer.
        </P>
      </Section>

      <Section title="Cookies and Device Information">
        <P>
          We may use cookies, local storage, and similar technologies for authentication, session
          management, security, preferences, analytics, and service reliability. We may also collect
          browser type, operating system, device identifiers, IP address, connection information, and
          interaction data.
        </P>
      </Section>

      <Section title="Security">
        <P>
          We use technical and organizational safeguards designed to protect user data, including encrypted
          credential storage, access controls, audit-oriented operational practices, and isolated machine
          environments. No method of transmission or storage is completely secure, so we cannot guarantee
          absolute security.
        </P>
      </Section>

      <Section title="Retention and Deletion">
        <P>
          We retain information for as long as needed to provide the service, comply with legal obligations,
          resolve disputes, enforce agreements, preserve security, and maintain backups. You may request
          deletion of account data or connected integration data through {supportContact}. Some information
          may remain in backups or logs for a limited period.
        </P>
      </Section>

      <Section title="Your Choices">
        <UL>
          <li>You can disconnect integrations to stop future access by OpenClaw Machines.</li>
          <li>You can revoke connected-service access from the third-party provider where supported.</li>
          <li>You can request access, correction, or deletion of your account information.</li>
          <li>You can stop using the service and request account closure.</li>
        </UL>
      </Section>

      <Section title="Changes">
        <P>
          We may update this Privacy Policy from time to time. When we make material changes, we will
          update the date above and, where appropriate, provide additional notice.
        </P>
      </Section>
    </LegalLayout>
  );
}

export function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      description="Terms of Service for OpenClaw Machines."
      canonicalPath="/terms"
    >
      <Section title="Agreement">
        <P>
          These Terms of Service govern your access to and use of OpenClaw Machines. By using the service,
          you agree to these Terms. If you are using the service on behalf of an organization, you represent
          that you have authority to bind that organization.
        </P>
      </Section>

      <Section title="Service">
        <P>
          OpenClaw Machines provides hosted infrastructure for creating and operating sandboxed AI agent
          machines, browser workspaces, web terminals, backups, and third-party integrations. Features may
          change as the service evolves.
        </P>
      </Section>

      <Section title="License and Fair Use">
        <P>
          Subject to these Terms, OpenClaw Machines grants you a limited, non-exclusive, non-transferable,
          revocable right to access and use the service for your internal personal or business purposes.
          Access must remain within reasonable operational limits and may be restricted if usage is
          excessive, abusive, automated in a harmful way, or likely to impair the service for others.
        </P>
      </Section>

      <Section title="Accounts and Access">
        <UL>
          <li>You are responsible for the accuracy of account information and for activity under your account.</li>
          <li>You must keep authentication credentials, API keys, and tokens secure.</li>
          <li>You must promptly notify us if you believe your account or machine has been compromised.</li>
          <li>You may not share access in a way that bypasses workspace permissions or security controls.</li>
        </UL>
      </Section>

      <Section title="User Content and Agents">
        <P>
          You retain ownership of code, files, prompts, credentials, data, and other content you submit to
          or create through the service. You grant OpenClaw Machines the rights needed to host, process,
          transmit, back up, and display that content solely to provide and operate the service.
        </P>
        <P>
          You are responsible for the instructions you give agents and for reviewing agent actions and
          outputs before relying on them. AI agents may make mistakes, take unexpected actions, or produce
          inaccurate content.
        </P>
      </Section>

      <Section title="Third-Party Services and Integrations">
        <P>
          The service may allow you to connect third-party services, applications, APIs, accounts, model
          providers, communication tools, code hosts, productivity tools, and other external systems. Your
          use of those services is governed by their own terms and policies. You are responsible for having
          the rights and permissions needed to connect and use those services.
        </P>
        <P>
          When you authorize a connection, OpenClaw Machines may access, store, process, and transfer data
          from that service only as needed to provide the integration features, workflows, and agent tools
          you enable, or as otherwise directed by you.
        </P>
      </Section>

      <Section title="Acceptable Use">
        <P>You may not use OpenClaw Machines to:</P>
        <UL>
          <li>Violate laws, regulations, contracts, intellectual property rights, or privacy rights.</li>
          <li>Access systems, accounts, data, or networks without authorization.</li>
          <li>Distribute malware, phishing, spam, abusive automation, or harmful content.</li>
          <li>Interfere with the security, integrity, availability, or performance of the service or third-party services.</li>
          <li>Exfiltrate data, evade rate limits, bypass access controls, or conceal abusive activity.</li>
          <li>Use agents or automation in high-risk settings where failure could cause physical harm, legal harm, or significant financial loss without appropriate human review.</li>
        </UL>
      </Section>

      <Section title="Payment and Availability">
        <P>
          Paid plans, usage limits, and billing terms are shown in the product or order flow when applicable.
          We may modify, suspend, or discontinue parts of the service, and we may impose reasonable limits
          to protect reliability, security, and other users.
        </P>
      </Section>

      <Section title="Intellectual Property">
        <P>
          OpenClaw Machines and its software, branding, design, documentation, and service materials are
          owned by us or our licensors. These Terms do not grant you any right to use our marks except as
          necessary to identify the service.
        </P>
      </Section>

      <Section title="Termination">
        <P>
          You may stop using the service at any time. We may suspend or terminate access if you violate
          these Terms, create risk for the service or other users, fail to pay applicable fees, or if
          required by law.
        </P>
      </Section>

      <Section title="Disclaimers">
        <P>
          The service is provided "as is" and "as available" without warranties of any kind, whether
          express, implied, or statutory, including warranties of merchantability, fitness for a particular
          purpose, title, and non-infringement. We do not guarantee that the service or AI outputs will be
          uninterrupted, secure, accurate, or error-free.
        </P>
      </Section>

      <Section title="Limitation of Liability">
        <P>
          To the maximum extent permitted by law, OpenClaw Machines will not be liable for indirect,
          incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost
          data, business interruption, or unauthorized access, even if we have been advised of the
          possibility of those damages.
        </P>
      </Section>

      <Section title="Changes and Contact">
        <P>
          We may update these Terms from time to time. The updated version will be posted here with a new
          effective date. Questions about these Terms can be sent through {supportContact}.
        </P>
      </Section>
    </LegalLayout>
  );
}
